/**
 * FolderSelect Component
 *
 * Dropdown showing folder hierarchy as breadcrumb paths.
 * Used in CreateTemplateDialog and CreateFolderDialog.
 */

import { useMemo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { buildFolderBreadcrumb } from '@/lib/email-templates'
import type { EmailFolder } from '@/lib/email-templates/folder-types'

interface FolderSelectProps {
  folders: EmailFolder[]
  value: number | null
  onChange: (folderId: number | null) => void
  /** When set, folders at this level or deeper are disabled (can't accept children) */
  maxSelectableLevel?: number
  placeholder?: string
}

export function FolderSelect({
  folders,
  value,
  onChange,
  maxSelectableLevel,
  placeholder = 'Select folder...',
}: FolderSelectProps) {
  // Build breadcrumb labels for each folder
  const folderOptions = useMemo(() => {
    return folders
      .sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
        return a.name.localeCompare(b.name)
      })
      .map((folder) => ({
        id: folder.id,
        label: buildFolderBreadcrumb(folder.id, folders),
        level: folder.level,
        disabled: maxSelectableLevel ? folder.level >= maxSelectableLevel : false,
      }))
  }, [folders, maxSelectableLevel])

  return (
    <Select
      value={value?.toString() ?? 'root'}
      onValueChange={(val) => onChange(val === 'root' ? null : parseInt(val, 10))}
    >
      <SelectTrigger className="rounded-lg">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="root">(Root level)</SelectItem>
        {folderOptions.map((option) => (
          <SelectItem
            key={option.id}
            value={option.id.toString()}
            disabled={option.disabled}
          >
            <span style={{ paddingLeft: `${(option.level - 1) * 12}px` }}>
              {option.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
